import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as UploadIcon } from "assets/images/icons/icn_btn_export_pressed.svg";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_btn_bin.svg";
import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckClearIcon } from "assets/images/icons/checkbox_clear.svg";
import { ReactComponent as CheckFillIcon } from "assets/images/icons/checkbox_fiilled.svg";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { MenuSelect } from "components/Select/menuSelect";
import UploadView from "./UploadView";

const MainView = ({
  form,
  proxyDevices,
  selectedProxyUuid,
  setSelectedProxyUuid,
  entries,
  selectedEntryIds,
  onCloseModal,
  onAddEntry,
  onDeleteSelected,
  onUpdateEntry,
  onToggleAllEntries,
  onToggleEntry,
  onUploadEntries,
  canSave,
  onSave,
}) => {
  const theme = useTheme();
  const viewRef = useRef(null);
  const [showUploadView, setShowUploadView] = useState(false);
  const hasNoProxy = proxyDevices.length === 0;
  const proxyOptions = proxyDevices.map((device) => ({
    value: device.proxyUuid,
    label: `${device.deviceName} (${device.linkStatus || "unknown"})`,
    disabled: device.linkStatus !== "online",
  }));
  const viewWidth = viewRef.current?.offsetWidth || 0;
  const viewHeight = viewRef.current?.offsetHeight || 0;
  const marginTop = 40;

  return (
    <Box ref={viewRef} sx={{ pl: 1, pr: 0, display: "flex", flexDirection: "column", position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "10px",
          }}
        >
          Discover Settings
        </Typography>
        <Button
          onClick={onCloseModal}
          sx={{
            minWidth: 36,
            p: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={form.getRowStyle()}>
        <Box sx={form.getLabelStyle()}>
          Selected Proxy
        </Box>
        <MenuSelect
          width={280}
          disabled={hasNoProxy}
          value={selectedProxyUuid}
          placeholder="Select a Scan Proxy"
          options={proxyOptions}
          onChange={(option) => setSelectedProxyUuid(option.value)}
        />
      </Box>

      <Box
        sx={{
          ...form.getRowStyle(),
          justifyContent: "flex-end",
          marginRight: "20px",
        }}
      >
        <OutlineRectangleButton
          startIcon={<AddIcon />}
          height="32px"
          onClick={onAddEntry}
        >
          Add
        </OutlineRectangleButton>
        <OutlineRectangleButton
          startIcon={<UploadIcon />}
          height="32px"
          onClick={() => setShowUploadView(true)}
        >
          Upload
        </OutlineRectangleButton>
        {selectedEntryIds.length > 0 && (
          <OutlineRectangleButton
            startIcon={<BinIcon />}
            height="32px"
            onClick={onDeleteSelected}
          >
            Delete
            <Box
              sx={{
                minWidth: 24,
                height: 20,
                ml: 0.5,
                px: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "15px",
                color: theme.palette.primary.white,
                backgroundColor: theme.palette.primary.main,
              }}
            >
              {selectedEntryIds.length}
            </Box>
          </OutlineRectangleButton>
        )}
      </Box>

      <Box sx={form.getTableStyle()}>
        <TableContainer
          component={Paper}
          sx={{ "&.MuiPaper-root": { boxShadow: "none", border: "none" } }}
          style={{ height: "300px", overflowY: "auto" }}
        >
          <Table
            stickyHeader
            sx={{
              minWidth: 680,
              borderCollapse: "collapse",
              "& td, & th": { border: "none" },
              "& thead tr": { borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}` },
              "& tbody tr:nth-of-type(odd)": { backgroundColor: "rgb(246, 246, 246)" },
              "& tbody tr:nth-of-type(even)": { backgroundColor: theme.palette.primary.white },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ backgroundColor: "#fff" }}>
                  <Checkbox
                    icon={<CheckDefIcon width="25px" height="25px" />}
                    checkedIcon={<CheckFillIcon width="25px" height="25px" />}
                    indeterminateIcon={<CheckClearIcon width="25px" height="25px" />}
                    checked={entries.length > 0 && selectedEntryIds.length === entries.length}
                    indeterminate={selectedEntryIds.length > 0 && selectedEntryIds.length < entries.length}
                    onChange={(event) => onToggleAllEntries(event.target.checked)}
                  />
                </TableCell>
                {["IP Address", "Username", "Password", "Spine/Leaf Role (Optional)"].map((label) => (
                  <TableCell
                    key={label}
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: theme.typography.customFontWeight[2],
                      whiteSpace: "nowrap",
                      px: 2,
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", padding: "20px" }}>
                    No Entry in the table
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        icon={<CheckDefIcon width="25px" height="25px" />}
                        checkedIcon={<CheckFillIcon width="25px" height="25px" />}
                        indeterminateIcon={<CheckClearIcon width="25px" height="25px" />}
                        checked={selectedEntryIds.includes(entry.id)}
                        onChange={() => onToggleEntry(entry.id)}
                      />
                    </TableCell>
                    {["ipAddress", "username", "password", "role"].map((key) => (
                      <TableCell key={key}>
                        <TextField
                          fullWidth
                          type="text"
                          name={`data-center-switch-${key}-${entry.id}`}
                          autoComplete={
                            key === "username" || key === "password"
                              ? "new-password"
                              : "off"
                          }
                          value={entry[key]}
                          onChange={(event) => onUpdateEntry(entry.id, key, event.target.value)}
                          inputProps={{ "aria-label": key }}
                          sx={{
                            ...form.getTextfieldStyle(true),
                            minWidth: key === "role" ? 150 : 120,
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ width: "100%", margin: "24px 0 16px", display: "flex", justifyContent: "center" }}>
        <OutlineButton width="100px" height="32px" sx={{ marginRight: "20px" }} onClick={onCloseModal}>
          Close
        </OutlineButton>
        <SolidButton width="100px" height="32px" disabled={!canSave} onClick={onSave}>
          Save
        </SolidButton>
      </Box>

      {showUploadView && (
        <Box
          style={{
            ...form.getMaskStyle(),
            width: viewWidth + 40 + "px",
            height: viewHeight + 60 + "px",
            transform: "translateY(-20px) translateX(-20px)",
          }}
        />
      )}
      {showUploadView && (
        <Box
          style={{
            ...form.getInnerViewStyle(zIndex),
            width: viewWidth + 40 + "px",
            transform: `translate(-20px, ${marginTop}px)`,
          }}
        >
          <Box style={form.getInnerContainerStyle()}>
            <UploadView
              form={form}
              onClose={() => setShowUploadView(false)}
              onApply={(uploadedEntries, replaceExisting) => {
                if (uploadedEntries.length > 0) {
                  onUploadEntries(uploadedEntries, replaceExisting);
                }
                setShowUploadView(false);
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MainView;
